import React, { FC, useEffect, useState } from 'react';
import Head from 'next/head';
import { t } from '@whitesharx/starfall-frontend-ttranslate';

import { AppComponentType } from '../_app';
import OnboardingPageLayout from '~/src/components/PageLayout/OnboardingPageLayout';
import useOnboardingGame from '~/src/components/Game/useOnboardingGame';
import { GameLayout, ResignModal, SearchModal } from '~/src/components/Game';
import { sounds } from '@whitesharx/starfall-frontend-ui-kit';
import ContinueTool from '~/src/components/Game/components/GameTools/ContinueTool';
import LeaveTool from '~/src/components/Game/components/GameTools/LeaveTool';
import OnboardingCustomApp, {
  OnboardingAppTypes,
  useOnboardingServices,
} from '~/src/components/CustomApp/OnboardingCustomApp';
import GameLeaveTool from '~/src/components/Game/components/GameLeaveTool';
import { getOrGenerateUserPseudoId } from '~/src/utils/getOrGenerateUserPseudoId';
import { removePersistedGame } from '~/src/components/Game/utils/game';
import { guestSignInOnOnboardingRequest } from '~/src/requests/guest-sign-on-onboarding';
import OnboardingGameSkeletonPage from '~/src/skeletons/OnboardingGame';
import { TAP_EVENTS, VIEW_EVENTS } from '~/src/services/analytics/contants';
import { GameMode } from '~/src/components/Game/constants';
import StalemateModal from '~/src/components/Game/components/Modals/StalemateModal';
import { MovesHistoryList } from '~/src/components/Game/components/MovesHistoryList';
import { GameResultModal } from '~/src/components/Game/components/GameResultModal';
import { movesHistoryStyles } from '~/styles/pages/game.css';

const OnboardingGameRealPage: FC<OnboardingAppTypes> = () => {
  const { Analytics, isDesktop } = useOnboardingServices();
  const [signInLoading, setSignInLoading] = useState(false);

  const {
    isSearching,
    isOpenResignModal,
    resign,
    openResignModal,
    closeResignModal,
    setCgApi,
    player,
    enemy,
    config,
    moves,
    downedFigures,
    shownFlash,
    isGameOver,
    gameResult,
    showPlayerEmoji,
    playerEmoji,
    enemyEmoji,
    game,
    continueOnboarding,
    isInitialized,
    isOnboarding,
    onTimerEnd,
    isOpenStalemateModal,
    undoStalemate,
    closeStalemateModal,
  } = useOnboardingGame(isDesktop);

  const onContinue = () => {
    Analytics.sendEvent(TAP_EVENTS.continueOnboarding);
    continueOnboarding();
  };

  const onLeave = async () => {
    setSignInLoading(true);

    guestSignInOnOnboardingRequest({ id: getOrGenerateUserPseudoId(), locale: 'en' })
      .then(({ token, player }) => {
        if (player) {
          localStorage.setItem('player', JSON.stringify(player));
        }
        if (token) {
          localStorage.setItem('apiToken', token);
        }
      })
      .catch((error) => {
        window.Sentry?.captureException(new Error('Failed while creating guest profile: ' + error));
      })
      .finally(() => {
        removePersistedGame();
        history.replaceState(null, '', '/');
        location.reload();
        setSignInLoading(false);
      });
  };

  const handleNavigationBarClick = () => {
    if (isGameOver) {
      if (isOnboarding) {
        onContinue();
      } else {
        onLeave();
      }
    } else {
      openResignModal();
    }
  };

  const gameResultButton = isOnboarding ? (
    <ContinueTool onContinue={onContinue} />
  ) : (
    <LeaveTool onLeave={onLeave} loading={signInLoading} />
  );

  useEffect(() => {
    sounds.prepare({ enabled: true, volume: 1 });
    sounds.load(['click']);

    Analytics.sendEvent(VIEW_EVENTS.mainMenuGame);
  }, []);

  return (
    <>
      <Head>
        <title>{t('browser-title.play')}</title>
      </Head>

      <SearchModal isOpen={isSearching} player={player} enemy={enemy} />
      <ResignModal isOpen={isOpenResignModal} onResign={resign} onRequestClose={closeResignModal} />
      <StalemateModal
        isOpen={isOpenStalemateModal}
        onUndoStalemate={undoStalemate}
        onRequestClose={closeStalemateModal}
      />

      <OnboardingPageLayout
        rightContainerContent={
          <>
            <MovesHistoryList className={movesHistoryStyles} />

            {isGameOver && !gameResult && <div className={movesHistoryStyles}>{gameResultButton}</div>}
          </>
        }
        onItemClick={handleNavigationBarClick}
        leftSlot={<GameLeaveTool onClick={handleNavigationBarClick} />}
        isDesktop={isDesktop}
        canShowDeeplink={game.isGameOver}
      >
        <GameResultModal mode={game.mode} isDesktop={isDesktop} buttons={[gameResultButton]} />

        <GameLayout
          player={player}
          enemy={enemy}
          config={config}
          downedFigures={downedFigures}
          shownFlash={shownFlash}
          onSet={setCgApi}
          leave={onLeave}
          isGameOver={isGameOver}
          onResign={openResignModal}
          emojiOptions={{ onSend: showPlayerEmoji, playerEmoji, enemyEmoji }}
          isDesktop={isDesktop}
          onContinue={onContinue}
          isInitialized={isInitialized}
          isOnboarding={isOnboarding}
          pulse={game.mode === GameMode.RAPID30 && !game.player?.pause && moves.length < 10}
          reactOnLittleTime={game.mode === GameMode.RAPID30}
          onTimerEnd={onTimerEnd}
          leaveLoading={signInLoading}
        />
      </OnboardingPageLayout>
    </>
  );
};

const OnboardingGamePage: FC<OnboardingAppTypes> & AppComponentType = (props) =>
  props.skeleton ? <OnboardingGameSkeletonPage /> : <OnboardingGameRealPage {...props} />;

OnboardingGamePage.render = (props) => <OnboardingCustomApp {...props} />;

export default OnboardingGamePage;
